import { useEffect, useState } from 'react';
import {
  getFeedbackFor,
  saveFeedback,
  type ScanVerdict,
  type ScanFeedbackRecord,
} from '../../services/scanFeedback';

interface ScanFeedbackProps {
  mediaType: 'movie' | 'tv';
  mediaId: number;
  clusters: string[];
}

const VERDICT_OPTIONS: Array<{ verdict: ScanVerdict; label: string }> = [
  { verdict: 'landed', label: 'Landed' },
  { verdict: 'neutral', label: 'Neutral' },
  { verdict: 'didnt_land', label: "Didn't land" },
];

export default function ScanFeedback({ mediaType, mediaId, clusters }: ScanFeedbackProps) {
  const [verdict, setVerdict] = useState<ScanVerdict | null>(null);
  const [note, setNote] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    const existing = getFeedbackFor(mediaType, mediaId);
    if (existing) {
      setVerdict(existing.verdict);
      setNote(existing.note ?? '');
      setSavedAt(existing.timestamp);
    } else {
      setVerdict(null);
      setNote('');
      setSavedAt(null);
    }
  }, [mediaType, mediaId]);

  const persist = (nextVerdict: ScanVerdict, nextNote: string) => {
    const record: ScanFeedbackRecord = {
      mediaType,
      mediaId,
      verdict: nextVerdict,
      note: nextNote.trim() ? nextNote.trim() : undefined,
      clusters,
      timestamp: Date.now(),
    };
    saveFeedback(record);
    setSavedAt(record.timestamp);
  };

  const handleVerdict = (next: ScanVerdict) => {
    setVerdict(next);
    persist(next, note);
  };

  const handleNoteBlur = () => {
    if (!verdict) return;
    persist(verdict, note);
  };

  return (
    <div className="mt-5 rounded-xl border border-emerald-300/15 bg-black/30 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-200/80">Did this scan land?</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-300/80">
        Stored only in your browser. No analytics, no remote writes.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {VERDICT_OPTIONS.map((option) => {
          const active = verdict === option.verdict;
          return (
            <button
              key={option.verdict}
              type="button"
              onClick={() => handleVerdict(option.verdict)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                active
                  ? 'border-emerald-300/60 bg-emerald-300/15 text-emerald-100'
                  : 'border-white/15 bg-white/[0.04] text-gray-200 hover:border-emerald-300/30 hover:text-emerald-100'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {verdict && (
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={handleNoteBlur}
          placeholder="One sentence (optional) — what did it touch or miss?"
          rows={2}
          className="mt-3 w-full resize-none rounded-lg border border-white/10 bg-black/30 p-2 text-sm text-gray-200 placeholder:text-gray-500 focus:border-emerald-300/40 focus:outline-none"
        />
      )}

      {savedAt && (
        <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-emerald-200/60">
          Saved locally
        </p>
      )}
    </div>
  );
}
